import { useId, type InputHTMLAttributes } from "react";
import { cx } from "./cx";
import { FieldLabel, FieldNote } from "./fieldParts";

export interface CodeInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "value" | "onChange" | "type"> {
  /** Default "6-digit code". */
  label?: string;
  /** Where the code is (SMS or inbox). Kept until there's an error. */
  hint?: string;
  /** Says what happened and what to do next. Never clear the entry on a wrong code. */
  error?: string;
  value: string;
  /** Receives digits only, capped at length. Pasting "123 456" gives "123456". */
  onChange: (code: string) => void;
  /** Default 6. */
  length?: number;
}

/** A one-time code as one field, not six boxes: it pastes, autofills from an SMS, and a
 *  screen reader reads it as one number. */
export function CodeInput({ label = "6-digit code", hint, error, value, onChange, length = 6, id, disabled, className, ...rest }: CodeInputProps) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const noteId = `${inputId}-note`;

  return (
    <label htmlFor={inputId} className={cx("block", className)}>
      {label && <FieldLabel>{label}</FieldLabel>}
      <input
        id={inputId}
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        pattern="[0-9]*"
        placeholder={"–".repeat(length)}
        disabled={disabled}
        aria-invalid={error ? true : undefined}
        aria-describedby={error || hint ? noteId : undefined}
        value={value}
        onChange={(event) => onChange(event.target.value.replace(/\D/g, "").slice(0, length))}
        className={cx(
          "block h-16 w-full rounded-sm border px-4 text-center indent-[0.42em] tracking-[0.42em] outline-none inset-shadow-default [transition:var(--transition-control)]",
          "font-mono text-[32px] leading-none font-medium focus:shadow-focus",
          disabled ? "cursor-not-allowed bg-paper-2 text-ink-300" : "bg-surface-card text-text-strong",
          error ? "border-danger-500" : "border-border-default focus:border-green-700",
        )}
        {...rest}
      />
      <FieldNote id={noteId} error={error} hint={hint} />
    </label>
  );
}
